/* eslint-disable @typescript-eslint/no-explicit-any */
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { BiogenFunction, Channel } from 'basics/enums/maya.enums';
import useGetCRMDropMaterials, { useGetCRMDropMaterialsLazy } from 'basics/graphql/queries/getCRMDropMaterials';
import useGetDropMaterials, { useGetDropMaterialsLazy } from 'basics/graphql/queries/getDropMaterials';
import useDropMaterialsOptions from 'basics/options/dropMaterials.options';
import { DropMaterialsOptionType } from 'basics/types/dropMaterials.types';
import { CepStateType, DropMaterialsStateType } from 'basics/types/maya.types';
import { warningToast } from 'basics/utils/toast';
import {
  CustomerInitiative,
  DropMaterialsFilter,
  Products,
} from 'generated/maya.types';
import cepState from 'states/cep.states';
import dropMaterials from 'states/dropMaterials.states';

type FetchTacticDropMaterialsOptionsParams = {
  country?: string;
  product?: string;
  executingTeam?: string;
};

const DROP_MATERIAL_CHANNELS: Channel[] = [
  Channel.f2fEvent,
  Channel.f2fMeeting,
  Channel.hybridEvent,
  Channel.oneToOne,
  Channel.oneToOneDropMaterial,
];

const isDropMaterialAvailableForExecutingTeam = (
  dropMaterial: Products,
  executingTeam?: string,
): boolean => {
  switch (executingTeam) {
    case BiogenFunction.Commercial:
      return Boolean(dropMaterial.commercialFunctionFlag);
    case BiogenFunction.Medical:
      return Boolean(dropMaterial.medicalFunctionFlag);
    case BiogenFunction.MarketAccess:
      return Boolean(dropMaterial.marketAccessFunctionFlag);
    default:
      return false;
  }
};

const mapDropMaterialsToOptions = (dropMaterialsList: Products[]): DropMaterialsOptionType[] => {
  return dropMaterialsList.map((dropMaterial: Products): DropMaterialsOptionType => ({
    id: dropMaterial.productID as string,
    label: dropMaterial.productName as string,
    medical: dropMaterial.medicalFunctionFlag || false,
    commercial: dropMaterial.commercialFunctionFlag || false,
    marketAccess: dropMaterial.marketAccessFunctionFlag || false,
  }));
};

export const useFetchTacticDropMaterialsOptions = () => {
  const flags = useFlags();
  const { getDropMaterialsLazy } = useGetDropMaterialsLazy();
  const { getCRMDropMaterialsLazy } = useGetCRMDropMaterialsLazy();

  const fetchTacticDropMaterialsOptions = async ({
    country,
    product,
    executingTeam,
  }: FetchTacticDropMaterialsOptionsParams): Promise<DropMaterialsOptionType[]> => {
    if (!country) {
      return [];
    }

    const isInternalSource = Boolean(
      flags.mayaAkma1456EnableInternalDropMaterials ?? flags['maya-akma-1456-enable-internal-drop-materials'],
    );

    const queryFn = isInternalSource ? getDropMaterialsLazy : getCRMDropMaterialsLazy;

    const { data } = await queryFn({
      variables: {
        filters: {
          Country: country,
          ...(flags.mayaBdcfcsd697EnableFilterForDropMaterialsByProduct && { Product: product }),
        },
      },
    });

    const responseKey = isInternalSource ? 'getDropMaterials' : 'getCRMDropMaterials';

    const filteredDropMaterials = (data?.[responseKey] || []).filter((dropMaterial: Products) => {
      return isDropMaterialAvailableForExecutingTeam(dropMaterial, executingTeam);
    });

    return mapDropMaterialsToOptions(filteredDropMaterials);
  };

  return {
    fetchTacticDropMaterialsOptions,
  };
};

const useTacticDropMaterials = (
  executingTeam: string,
  channel: Channel,
  _setValue?: (fieldName: string, fieldValue: any) => void,
  _currentDropMaterialId?: string,
) => {
  const flags = useFlags();
  const { t } = useTranslation();
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const dropMaterialsStateValue = useSnapshot(dropMaterials) as DropMaterialsStateType;
  const currentProductExternalId = cepStateValue.cep?.product;

  const customerInitiative = cepStateValue.cep?.customerInitiative as CustomerInitiative | undefined;
  const [dropMaterialsValue, setDropMaterialsValue] = useState<Products[]>(dropMaterialsStateValue.dropMaterials);
  const dropMaterialsOptions = useDropMaterialsOptions(dropMaterialsValue);

  const country = customerInitiative?.country;

  const dropMaterialsFilter = useMemo<DropMaterialsFilter>(() => ({
    Country: country || '',
    ...(flags.mayaBdcfcsd697EnableFilterForDropMaterialsByProduct && { Product: currentProductExternalId }),
  }), [
    country,
    currentProductExternalId,
    flags.mayaBdcfcsd697EnableFilterForDropMaterialsByProduct,
  ]);

  const isCountryMissing = !country;
  const skip = isCountryMissing;

  const isInternalSource = Boolean(
    flags.mayaAkma1456EnableInternalDropMaterials ?? flags['maya-akma-1456-enable-internal-drop-materials'],
  );

  const {
    data: internalDropMaterialsData,
    loading: internalDropMaterialsLoading,
  } = useGetDropMaterials(dropMaterialsFilter, skip || !isInternalSource);

  const {
    data: crmDropMaterialsData,
    loading: crmDropMaterialsLoading,
  } = useGetCRMDropMaterials(dropMaterialsFilter, skip || isInternalSource);

  const dropMaterialsData = isInternalSource ? internalDropMaterialsData : crmDropMaterialsData;
  const apolloLoading = isInternalSource ? internalDropMaterialsLoading : crmDropMaterialsLoading;

  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    setHasLoaded(false);
  }, [country, currentProductExternalId]);

  useEffect(() => {
    if (!apolloLoading && !skip) {
      setHasLoaded(true);
    }
  }, [apolloLoading, skip]);

  const dropMaterialsLoading = apolloLoading || isCountryMissing || (!hasLoaded && flags.mayaBdcfcsd697EnableDropMaterials !== false);

  const filterDropMaterialsByExecutingTeam = useCallback((dropMaterialsDocuments: Products[]) => {
    return dropMaterialsDocuments.filter((dropMaterial) => {
      return isDropMaterialAvailableForExecutingTeam(dropMaterial, executingTeam);
    });
  }, [executingTeam]);

  useEffect(() => {
    const dropMaterialsDocuments = dropMaterialsData.length ? dropMaterialsData : dropMaterialsStateValue.dropMaterials;
    const filteredDropMaterials = filterDropMaterialsByExecutingTeam(dropMaterialsDocuments);

    setDropMaterialsValue(filteredDropMaterials);

    const isValidChannel = DROP_MATERIAL_CHANNELS.includes(channel);

    if (hasLoaded && !filteredDropMaterials.length && executingTeam && isValidChannel) {
      warningToast(t('maya_drop_materials_warning'));
    }
  }, [executingTeam, channel, dropMaterialsData, dropMaterialsStateValue.dropMaterials, filterDropMaterialsByExecutingTeam, hasLoaded, t]);

  useEffect(() => {
    if (!apolloLoading && !skip && dropMaterialsData.length > 0) {
      dropMaterials.dropMaterials = dropMaterialsData;
      dropMaterials.isLoaded = true;
    }
  }, [apolloLoading, skip, dropMaterialsData]);

  return {
    dropMaterialsOptions,
    dropMaterialsLoading,
  };
};

export default useTacticDropMaterials;
